import { useQuery } from '@apollo/client'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    GetFilterInput,
    GetSortInput,
} from '@deltek/specification-client/core/Models/projectItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { debounce } from '@deltek/specification-client/core/Utilities/formUtils'
import { updateKpMainLocalStore } from '@deltek/specification-client/core/Utilities/localstorageUtils'
import Link from '@material-ui/core/Link'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React, { useCallback } from 'react'
import { ProjectFilterContext } from '../ProjectFilterContext'
import SearchTextField from './SearchTextField'

const useStyles = makeStyles((theme: Theme) => ({
    gridFilterCont: {
        marginTop: theme.spacing(1),
        position: 'relative',
        marginBottom: theme.spacing(1),
        marginLeft: 0,
        width: '100%',
        paddingLeft: theme.spacing(1),
    },
    filterItem: {
        marginRight: theme.spacing(9),
        marginLeft: theme.spacing(-1),
    },
    clearAllTitle: {
        fontSize: '1.4rem',
        cursor: 'pointer',
        position: 'absolute',
        bottom: theme.spacing(1),
        color: theme.palette.secondary.main,
    },
}))

interface ProjectGridFilterInterface {
    isFilterExpanded?: boolean
    setSkipCount?: (val: number) => void
    localStorageKey: string
    localStorageItemKey: (val: string) => string
    projectSortValues?: Partial<GetSortInput>[]
}
const ProjectGridFilter: React.FC<ProjectGridFilterInterface> = ({
    isFilterExpanded,
    setSkipCount,
    localStorageKey,
    localStorageItemKey,
    projectSortValues,
}) => {
    const classes = useStyles()
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const userId = currentUserInfoResponse?.currentUserInfo.kpUserId
    const { projectFilterValues, setProjectFilterValues } = React.useContext(
        ProjectFilterContext
    )
    const [inputValues, setInputValues] = React.useState<
        Partial<GetFilterInput>[]
    >(projectFilterValues)

    React.useEffect(() => {
        setInputValues(projectFilterValues)
    }, [projectFilterValues])

    const updateCacheData = (inputValue: Partial<GetFilterInput>[]) => {
        if (userId) {
            updateKpMainLocalStore({
                currentUserId: userId,
                pageKey: localStorageKey,
                itemKey: localStorageItemKey(userId),
                itemValue: JSON.stringify({
                    filterValues: inputValue,
                    filterView: isFilterExpanded,
                    isCardView: true,
                    sortValues: projectSortValues,
                }),
            })
        }
    }
    const debouncedRaiseOnInput = useCallback(
        debounce((inputValue: Partial<GetFilterInput>[]) => {
            if (setSkipCount) {
                setSkipCount(0)
            }
            setProjectFilterValues(inputValue)
            updateCacheData(inputValue)
        }),
        [inputValues]
    )
    const onSearchTextFieldValueChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const filterItems = [...inputValues]
        const isFilterApplied = filterItems.findIndex(
            (item: Partial<GetFilterInput>) =>
                item.itemField === event.currentTarget.name
        )
        if (isFilterApplied > -1) {
            if (event.currentTarget.value) {
                filterItems[isFilterApplied].filterValues = [
                    event.currentTarget.value,
                ]
            } else {
                filterItems.splice(isFilterApplied, 1)
            }
        } else {
            filterItems.push({
                itemField: event.currentTarget.name,
                filterValues: [event.currentTarget.value],
            })
        }
        setInputValues(filterItems)
        debouncedRaiseOnInput(filterItems)
    }

    const clearAllFilters = () => {
        if (projectFilterValues && projectFilterValues.length) {
            if (setSkipCount) {
                setSkipCount(0)
            }
            setInputValues([])
            setProjectFilterValues([])
            updateCacheData([])
        }
    }

    return (
        <GridContainer
            spacing={2}
            justifyContent="flex-start"
            className={classes.gridFilterCont}
        >
            <GridItem data-testid="projectGroup" className={classes.filterItem}>
                <SearchTextField
                    label="Project Group"
                    placeholder="Filter"
                    name="groupName"
                    onChange={onSearchTextFieldValueChange}
                    value={currentFilterValue(inputValues, 'groupName')}
                />
            </GridItem>
            <GridItem data-testid="projectName" className={classes.filterItem}>
                <SearchTextField
                    label="Project Name"
                    placeholder="Filter"
                    name="name"
                    onChange={onSearchTextFieldValueChange}
                    value={currentFilterValue(inputValues, 'name')}
                />
            </GridItem>
            <GridItem
                data-testid="projectNumber"
                className={classes.filterItem}
            >
                <SearchTextField
                    label="Project#"
                    placeholder="0001"
                    name="number"
                    onChange={onSearchTextFieldValueChange}
                    value={currentFilterValue(inputValues, 'number')}
                />
            </GridItem>
            <GridItem>
                <Link
                    data-testid={'clear-all-testid'}
                    underline={'none'}
                    className={classes.clearAllTitle}
                    onClick={clearAllFilters}
                    onKeyDown={onEnterKeyDown(clearAllFilters)}
                    tabIndex={0}
                >
                    Clear Filters
                </Link>
            </GridItem>
        </GridContainer>
    )
}

const currentFilterValue = (
    filterItems: Partial<GetFilterInput>[],
    currentFilterName: string
) => {
    const value = filterItems.find((item: Partial<GetFilterInput>) => {
        return item.itemField === currentFilterName
    })
    if (value && value.filterValues) {
        return value.filterValues.join(',')
    }
    return ''
}

export default ProjectGridFilter
